import { defineStore } from 'pinia'
import { useRequest } from 'composables/useRequest.js'

const CACHE_TTL = 5 * 60 * 1000 // 5 minutes

function emptyEventState() {
  return {
    attendees: [],
    fetchedAt: null,
    loading: false,
    error: null,
  }
}

// Keyed by event id — the same host can move between several events' attendee
// tabs in one session, and each event needs its own cache entry. Deliberately
// separate from `stores/attendees.js` (the global, unscoped list): conflating
// the two singular-cache shapes is what made the old page show every attendee.
export const useEventAttendeesStore = defineStore('eventAttendees', {
  state: () => ({
    byEventId: {}, // { [eventId]: { attendees, fetchedAt, loading, error } }
  }),

  getters: {
    stateFor: (state) => (eventId) => state.byEventId[eventId] || emptyEventState(),

    isCacheValid: (state) => (eventId) => {
      const entry = state.byEventId[eventId]
      return !!entry && entry.fetchedAt !== null && Date.now() - entry.fetchedAt < CACHE_TTL
    },
  },

  actions: {
    ensureEvent(eventId) {
      if (!this.byEventId[eventId]) this.byEventId[eventId] = emptyEventState()
      return this.byEventId[eventId]
    },

    async fetchIfStale(eventId) {
      if (this.isCacheValid(eventId)) return
      await this.fetchAttendees(eventId)
    },

    async fetchAttendees(eventId) {
      const entry = this.ensureEvent(eventId)
      entry.loading = true
      entry.error = null
      try {
        const { axiosRequest } = useRequest()
        const res = await axiosRequest('GET', `/api/v1/events/${eventId}/attendees`, {}, false)
        if (!res.js_error && res.response.success) {
          entry.attendees = res.response.data
          entry.fetchedAt = Date.now()
        } else {
          entry.error = 'Failed to load attendees.'
        }
      } catch {
        entry.error = 'Something went wrong.'
      } finally {
        entry.loading = false
      }
    },

    invalidate(eventId) {
      if (eventId) {
        const entry = this.byEventId[eventId]
        if (entry) entry.fetchedAt = null
      } else {
        this.byEventId = {}
      }
    },
  },
})
